/**
 * ESCHER FALLS — 面の「解の姿」を取り出す純ロジック。
 *
 * 全面クリア画面（METAGAME-SPEC §4）で、クリアしたステージの縮図を並べ、
 * そこに順番に水を通す演出に使う。**描画はしない**（`ui/finale.ts` の担当）。
 */

import type { Azimuth, Level, PortId, ScreenPos, ViewPose, ViewRelation } from '../core/types';
import { expandPieces, findGoalPort, findSourcePort } from '../core/pieces';
import { buildGraph, isSolvable } from '../core/graph';
import { RELATION_COUNT, toScreen } from '../core/projection';

/**
 * 関係を代表する姿勢。`relationOf(poseForRelation(r)) === r` が成り立つ。
 * 真上（関係 8）は4姿勢が同じ関係なので、方位 0 を代表に選ぶ。
 */
export function poseForRelation(relation: ViewRelation): ViewPose {
  if (relation === 8) return { azimuth: 0, elevation: 2 };
  if (relation < 4) return { azimuth: relation as Azimuth, elevation: 0 };
  return { azimuth: (relation - 4) as Azimuth, elevation: 1 };
}

/** 1面ぶんの縮図。座標は `0..1` に正規化済み（縦横比は保たれている）。 */
export interface LevelThumbnail {
  readonly levelId: string;
  readonly name: string;
  /** 解の接続関係。見つからなければ `null`（その面は縮図を描かない）。 */
  readonly relation: ViewRelation | null;
  /** 水源からゴールまでの経路を結ぶ折れ線。`0..1` の正規化座標。 */
  readonly path: readonly ScreenPos[];
  /** 経路以外の水路ポート（背景の淡い点として描く）。 */
  readonly rest: readonly ScreenPos[];
  /** 縦横比 (幅 / 高さ)。0 除算を避けるため最低 0.05。 */
  readonly aspect: number;
}

/** その面が解ける接続関係。ちょうど1つ見つかったときだけ返す。 */
export function findSolutionRelation(level: Level): ViewRelation | null {
  const pieces = expandPieces(level.pieces);
  let found: ViewRelation | null = null;
  for (let r = 0; r < RELATION_COUNT; r += 1) {
    if (!isSolvable(pieces, r as ViewRelation)) continue;
    if (found !== null) return null; // 2つ以上あるなら「解」とは呼べない
    found = r as ViewRelation;
  }
  return found;
}

/**
 * 水源からゴールまでのポート列を、経路順に返す。
 *
 * `planFlow` は「どのセグメントがいつ満ちるか」を返すが、こちらが欲しいのは
 * **1本の道すじ**なので、幅優先で親を辿って復元する。
 * 辺は決定的な順序に並んでいるので、同じ入力からは常に同じ道が出る。
 */
function tracePath(level: Level, relation: ViewRelation): PortId[] {
  const pieces = expandPieces(level.pieces);
  const graph = buildGraph(pieces, relation);
  const src = findSourcePort(pieces);
  const goal = findGoalPort(pieces);
  if (src === null || goal === null) return [];

  const parent = new Map<PortId, PortId | null>([[src.id, null]]);
  const queue: PortId[] = [src.id];
  for (let head = 0; head < queue.length; head += 1) {
    const cur = queue[head];
    if (cur === goal.id) break;
    for (const edge of graph.adjacency.get(cur) ?? []) {
      if (parent.has(edge.to)) continue;
      parent.set(edge.to, cur);
      queue.push(edge.to);
    }
  }
  if (!parent.has(goal.id)) return [];

  const out: PortId[] = [];
  for (let at: PortId | null = goal.id; at !== null; at = parent.get(at) ?? null) {
    out.push(at);
  }
  return out.reverse();
}

/**
 * 面を縮図に落とす。
 *
 * 解の関係で投影し、経路を `0..1` に正規化する。
 * **解が1つに定まらない面は `relation: null` で返す**（描かない）。
 */
export function buildThumbnail(level: Level): LevelThumbnail {
  const relation = findSolutionRelation(level);
  if (relation === null) {
    return { levelId: level.id, name: level.name, relation: null, path: [], rest: [], aspect: 1 };
  }

  const pieces = expandPieces(level.pieces);
  const graph = buildGraph(pieces, relation);
  // 縮図は「その面を解いたときに見えている絵」なので、関係に対応する姿勢で投影する。
  // 真上（関係 8）は方位で絵が回るだけで接続は同じなので、方位 0 を代表に選ぶ。
  const pose = poseForRelation(relation);
  const project = (id: PortId): ScreenPos => {
    const port = graph.ports.get(id);
    return port === undefined ? { x: 0, y: 0 } : toScreen(port.pos, pose);
  };

  const pathIds = tracePath(level, relation);
  const onPath = new Set(pathIds);
  const rawPath = pathIds.map(project);
  const rawRest = [...graph.ports.keys()].filter((id) => !onPath.has(id)).map(project);

  const all = [...rawPath, ...rawRest];
  if (all.length === 0) {
    return { levelId: level.id, name: level.name, relation, path: [], rest: [], aspect: 1 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of all) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const w = Math.max(maxX - minX, 1e-6);
  const h = Math.max(maxY - minY, 1e-6);
  // 長辺で割って縦横比を保つ。y は画面座標では上が正なので、描画側で反転する。
  const scale = 1 / Math.max(w, h);
  const norm = (p: ScreenPos): ScreenPos => ({
    x: (p.x - minX) * scale,
    y: (p.y - minY) * scale,
  });

  return {
    levelId: level.id,
    name: level.name,
    relation,
    path: rawPath.map(norm),
    rest: rawRest.map(norm),
    aspect: Math.max(0.05, w / h),
  };
}
